import React, { useEffect, useMemo, useReducer } from 'react'

import { FALLBACK_DOCUMENT_TYPE_ID } from '../../../models/DocumentType'
import { type DocumentRecord } from '../../../models/Document'

import { selectDocumentSummaryResult } from '../../../store/processDocument/document'
import { selectDocumentType } from '../../../store/processDocument/processDocument'
import { useAppSelector } from '../../../store/hooks'

import { ProcessDocumentFileUploadProgress, ProgressItem, ProgressItemWrapper } from '../ProcessDocumentFileUploadProgress/ProcessDocumentFileUploadProgress'
import { getDocumentFile } from '../utils'

const PROCESSING_TAKING_LONG_THRESHOLD_MS = 90_000

const isTakingLongerThanUsual = (startedAt: string | null): boolean =>
  !!startedAt && Date.now() - new Date(startedAt).getTime() >= PROCESSING_TAKING_LONG_THRESHOLD_MS

const useIsTakingLongerThanUsual = (startedAt: string | null): boolean => {
  const [, forceRerenderOnceSlow] = useReducer((x: number) => x + 1, 0)

  useEffect(() => {
    if (!startedAt || isTakingLongerThanUsual(startedAt)) {
      return
    }

    const remainingMs = PROCESSING_TAKING_LONG_THRESHOLD_MS - (Date.now() - new Date(startedAt).getTime())
    const timeoutId = setTimeout(forceRerenderOnceSlow, remainingMs)
    return () => clearTimeout(timeoutId)
  }, [startedAt])

  return isTakingLongerThanUsual(startedAt)
}

interface DocumentProcessingProgressProps {
  document: DocumentRecord
}

export const DocumentProcessingProgress: React.FC<DocumentProcessingProgressProps> = ({ document }) => {
  const { typeProcessing, summaryProcessing } = document

  const documentSummaryResult = useAppSelector(selectDocumentSummaryResult)
  const documentType = useAppSelector(selectDocumentType)

  const fileName = getDocumentFile(document)?.name

  const isProcessingTakingLongerThanUsual = useIsTakingLongerThanUsual(typeProcessing.startedAt)

  const progressItems: ProgressItem[] = useMemo(() => {
    const typeFinished = !!typeProcessing.finishedAt
    const typeSuccess = typeFinished && !!documentType
    const summarySuccess = !!summaryProcessing.finishedAt && !!documentSummaryResult

    const typeLoading = !typeFinished
    const summaryLoading = typeSuccess && !summarySuccess

    return [
      {
        isLoading: false,
        isSuccess: true,
        label: <ProgressItemWrapper isLoading={false}>Uploaded {fileName}</ProgressItemWrapper>,
      },
      {
        isLoading: typeLoading,
        isSuccess: typeSuccess,
        label: (
          <ProgressItemWrapper isLoading={typeLoading}>
            {documentType ? (
              documentType.systemDocumentTypeId === FALLBACK_DOCUMENT_TYPE_ID ? (
                <>
                  Couldn't match document type → Using <strong>{documentType.name}</strong> as fallback
                </>
              ) : (
                <>
                  Identified as <strong>{typeProcessing.label}</strong> → Applying <strong>{documentType.name}</strong> template
                </>
              )
            ) : (
              'Analyzing document type…'
            )}
          </ProgressItemWrapper>
        ),
      },
      {
        isLoading: summaryLoading,
        isSuccess: summarySuccess,
        label: (
          <ProgressItemWrapper isLoading={summaryLoading}>
            {documentSummaryResult
              ? 'Summary ready to review and sync'
              : summaryLoading && isProcessingTakingLongerThanUsual
                ? "This is taking longer than usual. We're still working on your file."
                : 'Extracting insights and creating summary…'}
          </ProgressItemWrapper>
        ),
      },
    ]
  }, [documentSummaryResult, documentType, fileName, isProcessingTakingLongerThanUsual, summaryProcessing, typeProcessing])

  return (
    <div className='d-flex flex-column p-4 gap-4 zocks-border-0.5 border zocks-border-color-gray-300'>
      <div className='d-flex flex-column align-items-center gap-1 text-center'>
        <h5>Processing document</h5>
        <span className='paragraph-medium zocks-text-gray-700'>
          Your AI Assistant is extracting insights and preparing updates for your CRM and financial systems.
        </span>
      </div>
      <ProcessDocumentFileUploadProgress items={progressItems} />
    </div>
  )
}
